import { defineFieldAdapter, type FieldTransformers } from '@libs/shared/frontend/helpers/define-field-adapter.helper';
import {
  entries as rEntries,
  isNonNullish as rIsNonNullish,
  map as rMap,
  mapValues as rMapValues,
  piped as rPiped,
  when as rWhen,
  pipe as rPipe,
  pickBy as rPickBy,
  conditional as rConditional,
  isStrictEqual as rIsStrictEqual,
  constant as rConstant,
} from 'remeda';

import { LOCALE } from '@/constants/locale';
import {
  CampaignRequestDistributionEnum,
  CampaignRequestStatusEnum,
  CampaignRequestTargetingTypeEnum,
  CreativeTypeEnum,
  type components,
  type operations,
} from '@/libs/openapi';
import { toDateOnlyString, toLocalDate, toLowerDenomination, toUpperDenomination } from '@/utils/convert.util';
import { formatDate, formatDateInTimezone, formatEnumValue, formatNumber } from '@/utils/format.util';
import { timezone } from '@/utils/timezone.util';

const formatMonthKey = (key: string): string => {
  const [ year, month ] = key.split('-');
  return new Intl.DateTimeFormat(LOCALE, { month: 'short', year: 'numeric' }).format(new Date(Number(year), Number(month) - 1));
};

type CampaignRequestDateRow = Pick<
components['schemas']['CampaignRequestResponseDto'],
'startDate' | 'endDate' | 'timezone'
>;

const getCampaignTimezone = (row: CampaignRequestDateRow): string =>
  row.timezone ?? timezone.get();

interface CampaignRequestDateFieldTransforms {
  forLabel: string;
  forFormValue: string;
}

export const fromApiCampaignRequestFieldAdapter = defineFieldAdapter({
  fields: {
    status: formatEnumValue(CampaignRequestStatusEnum),
    createdAt: formatDate('withTime'),
    updatedAt: formatDate('withTime'),
    totalBudget: {
      forLabel: rWhen(rIsNonNullish<number | undefined>, rPiped(
        toUpperDenomination,
        formatNumber('currency'),
      )),
      forFormValue: rWhen(rIsNonNullish<number | undefined>, toUpperDenomination),
    },
    monthlyBudget: {
      forLabel: rWhen(rIsNonNullish<Record<string, number> | null | undefined>, (value) => rPipe(
        value,
        rEntries(),
        rMap(([ key, v ]) => ({
          key,
          label: formatMonthKey(key),
          value: rPipe(v, toUpperDenomination, formatNumber('currency')),
        })),
      )),
      forFormValue: rWhen(rIsNonNullish<Record<string, number> | null | undefined>, rMapValues<Record<string, number>, number>(toUpperDenomination)),
    },
    distribution: rWhen(
      rIsNonNullish<CampaignRequestDistributionEnum | null | undefined>,
      (value): string => rConditional(
        value,
        [ rIsStrictEqual(CampaignRequestDistributionEnum.FRONT_LOADED), rConstant('Front-loaded') ],
        [ rIsStrictEqual(CampaignRequestDistributionEnum.MONTHLY), rConstant('Custom monthly') ],
        formatEnumValue(CampaignRequestDistributionEnum),
      ),
    ),
    creativeType: rWhen(rIsNonNullish<CreativeTypeEnum | null | undefined>, formatEnumValue(CreativeTypeEnum)),
    targetingType: rWhen(
      rIsNonNullish<CampaignRequestTargetingTypeEnum | null | undefined>,
      (value): string => rConditional(
        value,
        [ rIsStrictEqual(CampaignRequestTargetingTypeEnum.NPI), rConstant('NPI') ],
        [ rIsStrictEqual(CampaignRequestTargetingTypeEnum.SPECIALTY_AUTH), rConstant('Specialty-auth') ],
        [ rIsStrictEqual(CampaignRequestTargetingTypeEnum.RON), rConstant('RoN') ],
        formatEnumValue(CampaignRequestTargetingTypeEnum),
      ),
    ),
    cost: {
      forLabel: rWhen(rIsNonNullish<number | undefined>, rPiped(toUpperDenomination, formatNumber('currency'))),
      forFormValue: rWhen(rIsNonNullish<number | undefined>, toUpperDenomination),
    },
    clickGoal: rWhen(rIsNonNullish<number | null | undefined>, formatNumber('decimal')),
  } satisfies FieldTransformers<components['schemas']['CampaignRequestResponseDto']>,
  meta: {
    getStartDateRelativeToTimezone: (row: CampaignRequestDateRow): CampaignRequestDateFieldTransforms | undefined => {
      if (row.startDate == null) {
        return undefined;
      }
      const campaignTimezone = getCampaignTimezone(row);
      return {
        forLabel: formatDateInTimezone('full', campaignTimezone)(row.startDate),
        forFormValue: toLocalDate(row.startDate, campaignTimezone),
      };
    },
    getEndDateRelativeToTimezone: (row: CampaignRequestDateRow): CampaignRequestDateFieldTransforms | undefined => {
      if (row.endDate == null) {
        return undefined;
      }
      const campaignTimezone = getCampaignTimezone(row);
      return {
        forLabel: formatDateInTimezone('full', campaignTimezone)(row.endDate),
        forFormValue: toLocalDate(row.endDate, campaignTimezone),
      };
    },
  },
}, 'strict');

type CampaignRequestFilterQuery = NonNullable<operations['CampaignRequestController_getMany']['parameters']['query']>;

export const toApiCampaignRequestFieldAdapter = defineFieldAdapter({
  fields: {
    startDate: rWhen(rIsNonNullish<string | undefined>, toDateOnlyString),
    endDate: rWhen(rIsNonNullish<string | undefined>, toDateOnlyString),
    totalBudget: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    cost: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    totalBudgetGte: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    totalBudgetLte: rWhen(rIsNonNullish<number | undefined>, toLowerDenomination),
    monthlyBudget: rWhen(rIsNonNullish<Record<string, number> | undefined>, (budget) => rPipe(
      budget,
      rMapValues(toLowerDenomination),
      rPickBy((value) => value !== 0),
    )),
  } satisfies FieldTransformers<components['schemas']['CampaignRequestResponseDto'] & Required<Pick<CampaignRequestFilterQuery, 'totalBudgetGte' | 'totalBudgetLte'>>>,
}, 'loose');
